import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface EmailsAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  disabled: boolean;
}

export default function EmailsAccordion({
  control,
  errors,
  disabled,
  watch,
}: EmailsAccordionProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'emails',
  });

  const menuItems = [
    { value: 'custom', label: 'Custom' },
    { value: 'home', label: 'Home' },
    { value: 'other', label: 'Other' },
    { value: 'work', label: 'Work' },
  ];

  const emailErrors = errors['emails'];
  const emailCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({
      type: '',
      primary: false,
      customType: '',
      address: '',
    });
  };

  return (
    <SharedProfileInformationAccordion
      title="Emails"
      count={emailCount}
      hasError={!!emailErrors}
      onAdd={handleOnAdd}
      disabled={disabled}
    >
      {fields.map((item, index) => {
        const typeField = watch(`emails.${index}.type`);

        return (
          <Box key={item.id}>
            <Stack
              gap={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ my: 1.5 }}
            >
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`emails.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      inputProps={{
                        id: `emails-type-${index}`,
                      }}
                      InputLabelProps={{
                        htmlFor: `emails-type-${index}`,
                      }}
                      name="emails"
                      select
                      disabled={disabled}
                      label={`type`}
                      error={!!emailErrors?.[index]?.type}
                      helperText={
                        emailErrors?.[index]?.type
                          ? 'This field is required.'
                          : ''
                      }
                      sx={{
                        minWidth: 150,
                        textTransform: 'capitalize',
                      }}
                    >
                      {menuItems.map((item) => (
                        <MenuItem value={item.value} key={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                {typeField === 'custom' && (
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`emails.${index}.customType`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `emails-customType-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `emails-customType-${index}`,
                        }}
                        disabled={disabled}
                        name="emails"
                        label={`Custom Type`}
                        error={!!emailErrors?.[index]?.customType}
                        helperText={
                          emailErrors?.[index]?.customType
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                )}
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`emails.${index}.address`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      error={!!emailErrors?.[index]?.address}
                      helperText={
                        emailErrors?.[index]?.address
                          ? 'This field is required.'
                          : ''
                      }
                      name="emails"
                      disabled={disabled}
                      autoComplete="off"
                      label={`Address`}
                      sx={{
                        minWidth: 225,
                      }}
                    />
                  )}
                />
              </Stack>

              <SharedProfileInformationItemActions
                index={index}
                totalCount={emailCount}
                disabled={disabled}
                onMoveDown={() => move(index, index + 1)}
                onMoveUp={() => move(index, index - 1)}
                onDelete={() => remove(index)}
              />
            </Stack>
            {index !== fields.length - 1 && <Divider />}
          </Box>
        );
      })}
    </SharedProfileInformationAccordion>
  );
}
